import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Globe, FileText, Code, BarChart3, Search, Plus, Trash2, Copy, Download,
  Loader2, Save, RefreshCw, TrendingUp, Eye, Users, ArrowUpRight
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SeoToolkit({ token }) {
  const [activeSection, setActiveSection] = useState("meta");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // SEO settings state
  const [meta, setMeta] = useState({ title: "", description: "", og_title: "", og_description: "", og_image: "", twitter_card: "summary_large_image", twitter_title: "", twitter_description: "", canonical_url: "" });
  const [robots, setRobots] = useState("");
  const [scripts, setScripts] = useState({ head_scripts: "", body_scripts: "" });
  const [keywords, setKeywords] = useState([]);
  const [newKeyword, setNewKeyword] = useState({ keyword: "", target_url: "/", priority: "medium" });
  const [sitemap, setSitemap] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [analyticsDays, setAnalyticsDays] = useState(30);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { loadSettings(); }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/seo/settings`, { headers });
      const d = res.data;
      if (d.meta) setMeta(d.meta);
      if (d.robots) setRobots(d.robots.content || "");
      if (d.scripts) setScripts(d.scripts);
      if (d.keywords) setKeywords(d.keywords);
    } catch { toast.error("Failed to load SEO settings"); }
    finally { setLoading(false); }
  };

  const saveMeta = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/seo/meta`, meta, { headers });
      toast.success("Meta tags saved");
    } catch { toast.error("Failed to save meta tags"); }
    finally { setSaving(false); }
  };

  const saveRobots = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/seo/robots`, { content: robots }, { headers });
      toast.success("robots.txt saved");
    } catch { toast.error("Failed to save robots.txt"); }
    finally { setSaving(false); }
  };

  const saveScripts = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/seo/scripts`, scripts, { headers });
      toast.success("Custom scripts saved");
    } catch { toast.error("Failed to save scripts"); }
    finally { setSaving(false); }
  };

  const generateSitemap = async () => {
    try {
      const res = await axios.get(`${API}/admin/seo/sitemap`, { headers });
      setSitemap(res.data);
      toast.success(`Sitemap generated with ${res.data.page_count} pages`);
    } catch { toast.error("Failed to generate sitemap"); }
  };

  const downloadSitemap = () => {
    if (!sitemap) return;
    const blob = new Blob([sitemap.sitemap_xml], { type: "application/xml" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "sitemap.xml"; a.click();
    URL.revokeObjectURL(url);
  };

  const addKeyword = async () => {
    if (!newKeyword.keyword.trim()) { toast.error("Enter a keyword"); return; }
    try {
      const res = await axios.post(`${API}/admin/seo/keywords`, newKeyword, { headers });
      setKeywords([...keywords, res.data.keyword]);
      setNewKeyword({ keyword: "", target_url: "/", priority: "medium" });
      toast.success("Keyword added");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to add keyword"); }
  };

  const removeKeyword = async (kw) => {
    try {
      await axios.delete(`${API}/admin/seo/keywords/${encodeURIComponent(kw)}`, { headers });
      setKeywords(keywords.filter(k => k.keyword !== kw));
      toast.success("Keyword removed");
    } catch { toast.error("Failed to remove keyword"); }
  };

  const loadAnalytics = async () => {
    try {
      const res = await axios.get(`${API}/admin/seo/analytics?days=${analyticsDays}`, { headers });
      setAnalytics(res.data);
    } catch { toast.error("Failed to load analytics"); }
  };

  useEffect(() => { if (activeSection === "analytics") loadAnalytics(); }, [activeSection, analyticsDays]);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  const sections = [
    { id: "meta", label: "Meta Tags", icon: Globe },
    { id: "sitemap", label: "Sitemap", icon: FileText },
    { id: "robots", label: "robots.txt", icon: FileText },
    { id: "analytics", label: "Analytics", icon: BarChart3 },
    { id: "keywords", label: "Keywords", icon: Search },
    { id: "scripts", label: "Scripts", icon: Code },
  ];

  if (loading) return <div className="seo-loading"><Loader2 className="w-5 h-5 animate-spin" /> Loading SEO toolkit...</div>;

  return (
    <div className="seo-toolkit" data-testid="seo-toolkit">
      <div className="seo-nav" data-testid="seo-nav">
        {sections.map(s => (
          <button key={s.id} data-testid={`seo-tab-${s.id}`} className={`seo-nav-btn ${activeSection === s.id ? "active" : ""}`} onClick={() => setActiveSection(s.id)}>
            <s.icon className="w-4 h-4" />{s.label}
          </button>
        ))}
      </div>

      <div className="seo-content">
        {/* META TAGS */}
        {activeSection === "meta" && (
          <div className="seo-section" data-testid="seo-meta-section">
            <Card className="seo-card">
              <CardHeader>
                <CardTitle className="seo-card-title"><Globe className="w-5 h-5 text-blue-400" /> Meta Tags Editor</CardTitle>
                <CardDescription>Control how your site appears in search results and social media shares.</CardDescription>
              </CardHeader>
              <CardContent className="seo-form">
                <div className="seo-field">
                  <label>Page Title</label>
                  <Input data-testid="seo-meta-title" value={meta.title} onChange={e => setMeta({ ...meta, title: e.target.value })} placeholder="Market Mop - Walmart Bulk Retirement Tool" />
                  <span className="seo-hint">{meta.title.length}/60 characters (recommended)</span>
                </div>
                <div className="seo-field">
                  <label>Meta Description</label>
                  <textarea data-testid="seo-meta-description" className="seo-textarea" value={meta.description} onChange={e => setMeta({ ...meta, description: e.target.value })} placeholder="Bulk retire Walmart items and export CSV data..." rows={3} />
                  <span className="seo-hint">{meta.description.length}/160 characters (recommended)</span>
                </div>
                <div className="seo-field">
                  <label>Canonical URL</label>
                  <Input data-testid="seo-meta-canonical" value={meta.canonical_url} onChange={e => setMeta({ ...meta, canonical_url: e.target.value })} placeholder="https://marketmop.com" />
                </div>

                <div className="seo-divider">Open Graph (Social Sharing)</div>
                <div className="seo-field-row">
                  <div className="seo-field">
                    <label>OG Title</label>
                    <Input data-testid="seo-og-title" value={meta.og_title} onChange={e => setMeta({ ...meta, og_title: e.target.value })} placeholder="Falls back to Page Title" />
                  </div>
                  <div className="seo-field">
                    <label>OG Image URL</label>
                    <Input data-testid="seo-og-image" value={meta.og_image} onChange={e => setMeta({ ...meta, og_image: e.target.value })} placeholder="https://marketmop.com/og-image.png" />
                  </div>
                </div>
                <div className="seo-field">
                  <label>OG Description</label>
                  <textarea data-testid="seo-og-description" className="seo-textarea" value={meta.og_description} onChange={e => setMeta({ ...meta, og_description: e.target.value })} placeholder="Falls back to Meta Description" rows={2} />
                </div>

                <div className="seo-divider">Twitter Card</div>
                <div className="seo-field-row">
                  <div className="seo-field">
                    <label>Card Type</label>
                    <Select value={meta.twitter_card} onValueChange={v => setMeta({ ...meta, twitter_card: v })}>
                      <SelectTrigger data-testid="seo-twitter-card"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="summary">Summary</SelectItem>
                        <SelectItem value="summary_large_image">Summary Large Image</SelectItem>
                        <SelectItem value="app">App</SelectItem>
                        <SelectItem value="player">Player</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="seo-field">
                    <label>Twitter Title</label>
                    <Input data-testid="seo-twitter-title" value={meta.twitter_title} onChange={e => setMeta({ ...meta, twitter_title: e.target.value })} placeholder="Falls back to OG/Page Title" />
                  </div>
                </div>
                <div className="seo-field">
                  <label>Twitter Description</label>
                  <textarea data-testid="seo-twitter-description" className="seo-textarea" value={meta.twitter_description} onChange={e => setMeta({ ...meta, twitter_description: e.target.value })} placeholder="Falls back to OG/Meta Description" rows={2} />
                </div>

                <Button data-testid="seo-save-meta" className="seo-save-btn" onClick={saveMeta} disabled={saving}>
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save Meta Tags
                </Button>
              </CardContent>
            </Card>

            {/* Preview */}
            <Card className="seo-card seo-preview-card">
              <CardHeader><CardTitle className="seo-card-title"><Eye className="w-5 h-5 text-green-400" /> Search Preview</CardTitle></CardHeader>
              <CardContent>
                <div className="seo-serp-preview" data-testid="seo-serp-preview">
                  <div className="serp-url">{meta.canonical_url || "https://marketmop.com"}</div>
                  <div className="serp-title">{meta.title || "Market Mop"}</div>
                  <div className="serp-description">{meta.description || "No description set."}</div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* SITEMAP */}
        {activeSection === "sitemap" && (
          <div className="seo-section" data-testid="seo-sitemap-section">
            <Card className="seo-card">
              <CardHeader>
                <CardTitle className="seo-card-title"><FileText className="w-5 h-5 text-amber-400" /> Sitemap Generator</CardTitle>
                <CardDescription>Generate an XML sitemap for search engine crawlers. Keywords with target URLs are automatically included.</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="seo-actions-row">
                  <Button data-testid="seo-generate-sitemap" onClick={generateSitemap} className="seo-action-btn">
                    <RefreshCw className="w-4 h-4" /> Generate Sitemap
                  </Button>
                  {sitemap && (
                    <Button data-testid="seo-download-sitemap" onClick={downloadSitemap} variant="outline">
                      <Download className="w-4 h-4" /> Download sitemap.xml
                    </Button>
                  )}
                  {sitemap && (
                    <Button variant="ghost" onClick={() => copyToClipboard(sitemap.sitemap_xml)}>
                      <Copy className="w-4 h-4" /> Copy XML
                    </Button>
                  )}
                </div>
                {sitemap && (
                  <div className="seo-sitemap-preview" data-testid="seo-sitemap-output">
                    <div className="seo-sitemap-info">{sitemap.page_count} page(s) in sitemap</div>
                    <pre className="seo-code-block">{sitemap.sitemap_xml}</pre>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* ROBOTS.TXT */}
        {activeSection === "robots" && (
          <div className="seo-section" data-testid="seo-robots-section">
            <Card className="seo-card">
              <CardHeader>
                <CardTitle className="seo-card-title"><FileText className="w-5 h-5 text-orange-400" /> robots.txt Editor</CardTitle>
                <CardDescription>Control which pages search engines can crawl.</CardDescription>
              </CardHeader>
              <CardContent className="seo-form">
                <textarea data-testid="seo-robots-content" className="seo-textarea seo-code-textarea" value={robots} onChange={e => setRobots(e.target.value)} rows={12} placeholder="User-agent: *&#10;Allow: /" />
                <Button data-testid="seo-save-robots" className="seo-save-btn" onClick={saveRobots} disabled={saving}>
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save robots.txt
                </Button>
              </CardContent>
            </Card>
          </div>
        )}

        {/* ANALYTICS */}
        {activeSection === "analytics" && (
          <div className="seo-section" data-testid="seo-analytics-section">
            <div className="seo-analytics-header">
              <Select value={analyticsDays.toString()} onValueChange={v => setAnalyticsDays(parseInt(v))}>
                <SelectTrigger className="seo-period-select" data-testid="seo-analytics-period"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">Last 7 days</SelectItem>
                  <SelectItem value="14">Last 14 days</SelectItem>
                  <SelectItem value="30">Last 30 days</SelectItem>
                  <SelectItem value="90">Last 90 days</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="ghost" size="sm" onClick={loadAnalytics}><RefreshCw className="w-4 h-4" /></Button>
            </div>

            {analytics ? (
              <>
                <div className="seo-analytics-stats">
                  <Card className="seo-stat-card">
                    <CardContent className="seo-stat-content">
                      <Eye className="w-5 h-5 text-blue-400" />
                      <div className="seo-stat-number">{analytics.total_views}</div>
                      <div className="seo-stat-label">Page Views</div>
                    </CardContent>
                  </Card>
                  <Card className="seo-stat-card">
                    <CardContent className="seo-stat-content">
                      <Users className="w-5 h-5 text-green-400" />
                      <div className="seo-stat-number">{analytics.unique_visitors}</div>
                      <div className="seo-stat-label">Unique Visitors</div>
                    </CardContent>
                  </Card>
                  <Card className="seo-stat-card">
                    <CardContent className="seo-stat-content">
                      <TrendingUp className="w-5 h-5 text-amber-400" />
                      <div className="seo-stat-number">{analytics.bounce_rate}%</div>
                      <div className="seo-stat-label">Bounce Rate</div>
                    </CardContent>
                  </Card>
                </div>

                {/* Daily Views Chart (simple bar) */}
                <Card className="seo-card">
                  <CardHeader><CardTitle className="seo-card-title"><BarChart3 className="w-5 h-5 text-blue-400" /> Daily Page Views</CardTitle></CardHeader>
                  <CardContent>
                    {analytics.daily_views.length > 0 ? (
                      <div className="seo-chart" data-testid="seo-daily-chart">
                        {(() => {
                          const maxViews = Math.max(...analytics.daily_views.map(d => d.views), 1);
                          return analytics.daily_views.map(d => (
                            <div key={d.date} className="seo-chart-bar-wrapper" title={`${d.date}: ${d.views} views`}>
                              <div className="seo-chart-bar" style={{ height: `${(d.views / maxViews) * 100}%` }} />
                              <div className="seo-chart-label">{d.date.slice(5)}</div>
                            </div>
                          ));
                        })()}
                      </div>
                    ) : <div className="seo-empty">No data for this period</div>}
                  </CardContent>
                </Card>

                {/* Top Pages & Referrers */}
                <div className="seo-analytics-tables">
                  <Card className="seo-card">
                    <CardHeader><CardTitle className="seo-card-title">Top Pages</CardTitle></CardHeader>
                    <CardContent>
                      {analytics.top_pages.length > 0 ? (
                        <div className="seo-list" data-testid="seo-top-pages">
                          {analytics.top_pages.map((p, i) => (
                            <div key={i} className="seo-list-item">
                              <span className="seo-list-name">{p.page}</span>
                              <span className="seo-list-value">{p.views} views</span>
                            </div>
                          ))}
                        </div>
                      ) : <div className="seo-empty">No page data</div>}
                    </CardContent>
                  </Card>
                  <Card className="seo-card">
                    <CardHeader><CardTitle className="seo-card-title">Top Referrers</CardTitle></CardHeader>
                    <CardContent>
                      {analytics.top_referrers.length > 0 ? (
                        <div className="seo-list" data-testid="seo-top-referrers">
                          {analytics.top_referrers.map((r, i) => (
                            <div key={i} className="seo-list-item">
                              <span className="seo-list-name">{r.referrer}</span>
                              <span className="seo-list-value">{r.count} visits</span>
                            </div>
                          ))}
                        </div>
                      ) : <div className="seo-empty">No referrer data yet</div>}
                    </CardContent>
                  </Card>
                </div>
              </>
            ) : <div className="seo-loading"><Loader2 className="w-5 h-5 animate-spin" /> Loading analytics...</div>}
          </div>
        )}

        {/* KEYWORDS */}
        {activeSection === "keywords" && (
          <div className="seo-section" data-testid="seo-keywords-section">
            <Card className="seo-card">
              <CardHeader>
                <CardTitle className="seo-card-title"><Search className="w-5 h-5 text-purple-400" /> Keyword Tracker</CardTitle>
                <CardDescription>Track target keywords for your site. Keywords with URLs are included in the sitemap.</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="seo-keyword-add" data-testid="seo-keyword-add-form">
                  <Input data-testid="seo-keyword-input" value={newKeyword.keyword} onChange={e => setNewKeyword({ ...newKeyword, keyword: e.target.value })} placeholder="Enter keyword..." className="seo-keyword-input" />
                  <Input data-testid="seo-keyword-url" value={newKeyword.target_url} onChange={e => setNewKeyword({ ...newKeyword, target_url: e.target.value })} placeholder="Target URL" className="seo-keyword-url" />
                  <Select value={newKeyword.priority} onValueChange={v => setNewKeyword({ ...newKeyword, priority: v })}>
                    <SelectTrigger className="seo-keyword-priority" data-testid="seo-keyword-priority"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="low">Low</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button data-testid="seo-add-keyword-btn" onClick={addKeyword}><Plus className="w-4 h-4" /> Add</Button>
                </div>

                {keywords.length > 0 ? (
                  <div className="seo-keyword-list" data-testid="seo-keyword-list">
                    {keywords.map((kw, i) => (
                      <div key={i} className="seo-keyword-item">
                        <div className="seo-keyword-info">
                          <span className="seo-keyword-text">{kw.keyword}</span>
                          <span className="seo-keyword-url-display"><ArrowUpRight className="w-3 h-3" />{kw.target_url}</span>
                        </div>
                        <div className="seo-keyword-meta">
                          <span className={`seo-priority-badge seo-priority-${kw.priority}`}>{kw.priority}</span>
                          <Button variant="ghost" size="sm" onClick={() => removeKeyword(kw.keyword)} data-testid={`seo-remove-keyword-${i}`}><Trash2 className="w-3 h-3 text-red-400" /></Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : <div className="seo-empty">No keywords tracked yet. Add your first keyword above.</div>}
              </CardContent>
            </Card>
          </div>
        )}

        {/* CUSTOM SCRIPTS */}
        {activeSection === "scripts" && (
          <div className="seo-section" data-testid="seo-scripts-section">
            <Card className="seo-card">
              <CardHeader>
                <CardTitle className="seo-card-title"><Code className="w-5 h-5 text-cyan-400" /> Custom Scripts</CardTitle>
                <CardDescription>Add tracking scripts like Google Analytics, Facebook Pixel, or any custom code.</CardDescription>
              </CardHeader>
              <CardContent className="seo-form">
                <div className="seo-field">
                  <label>Head Scripts (injected before &lt;/head&gt;)</label>
                  <textarea data-testid="seo-head-scripts" className="seo-textarea seo-code-textarea" value={scripts.head_scripts} onChange={e => setScripts({ ...scripts, head_scripts: e.target.value })} rows={8} placeholder="<!-- Google Analytics, Meta Pixel, etc. -->" />
                </div>
                <div className="seo-field">
                  <label>Body Scripts (injected before &lt;/body&gt;)</label>
                  <textarea data-testid="seo-body-scripts" className="seo-textarea seo-code-textarea" value={scripts.body_scripts} onChange={e => setScripts({ ...scripts, body_scripts: e.target.value })} rows={8} placeholder="<!-- Chat widgets, tracking pixels, etc. -->" />
                </div>
                <Button data-testid="seo-save-scripts" className="seo-save-btn" onClick={saveScripts} disabled={saving}>
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save Scripts
                </Button>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
